import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircleIcon, CheckCircle2Icon, PlusIcon, SearchIcon } from 'lucide-react';
import { ChatRow } from '../components/ChatRow';
import { BottomNav } from '../components/BottomNav';
import { WhatsAppConnectionModal } from '../components/WhatsAppConnectionModal';
import type { WhatsAppChatSummary } from '../api/whatsapp';
import { useClients, type ClientRecord } from '../hooks/useClients';
import { useWhatsAppChats } from '../hooks/useWhatsAppChats';
import { useSupabaseChats } from '../hooks/useSupabaseChats';
import { usePriorityLeadChats } from '../hooks/usePriorityLeadChats';
import { useWhatsAppStatus } from '../hooks/useWhatsAppStatus';
import { phoneNumberToWhatsAppJid, whatsappJidToPhone } from '../../lib/whatsapp/jid';
import { supabase } from '../lib/supabase';
import { LeadConversionModal } from '../components/LeadConversionModal';
import { useLeadLabelMap, useLeadLabelMutation, useArchiveLead, useUnarchiveLead } from '../hooks/useLeadLabels';
import { useConvertLead } from '../hooks/useConvertLead';
import type { LeadLabel } from '../types/leads';
import { LeadStatusFilterChips } from '../components/LeadStatusFilterChips';
import { ClientStatusFilterChips } from '../components/ClientStatusFilterChips';
import { MediumFilterChips } from '../components/MediumFilterChips';

type ClientStatus = 'lead' | 'active' | 'past' | 'cold';

interface ChatListRow {
  id: string;
  clientId?: string;
  name: string;
  phone: string;
  status: ClientStatus;
  lastMessage: string;
  timestamp: string;
  timestampMs: number | null;
  unread: boolean;
  unreadCount: number;
  jid: string | null;
  kind: 'lead' | 'client';
  leadLabel?: LeadLabel;
  medium?: 'online' | 'offline' | null;
}

function formatRelativeTime(timestamp?: number | null): string {
  if (!timestamp) return 'No interactions yet';
  const diffMs = Date.now() - timestamp;
  if (diffMs < 60_000) return 'Just now';
  const diffMinutes = Math.floor(diffMs / 60_000);
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hr${diffHours > 1 ? 's' : ''} ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

export function Chats() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatListScrollRef = useRef<HTMLDivElement | null>(null);

  // Initialize state from URL params or defaults
  const [audienceTab, setAudienceTab] = useState<'leads' | 'clients' | 'archived'>(() => {
    const tab = searchParams.get('audienceTab');
    return (tab === 'leads' || tab === 'clients' || tab === 'archived') ? tab : 'leads';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [leadActionBanner, setLeadActionBanner] = useState<
    { type: 'success' | 'error'; text: string } | null
  >(null);
  const [conversionTarget, setConversionTarget] = useState<ChatListRow | null>(null);
  const [conversionError, setConversionError] = useState<string | null>(null);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [selectedLeadStatuses, setSelectedLeadStatuses] = useState<Set<LeadLabel>>(new Set());
  const [selectedClientStatuses, setSelectedClientStatuses] = useState<Set<'active' | 'past'>>(new Set());
  const [selectedMediums, setSelectedMediums] = useState<Set<'online' | 'offline'>>(new Set());
  const [showUnreadLeads, setShowUnreadLeads] = useState(false);
  const [showUnreadClients, setShowUnreadClients] = useState(false);
  const [loadedDayRanges, setLoadedDayRanges] = useState<number[]>([30]); // Track loaded time windows
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [olderChatsBatches, setOlderChatsBatches] = useState<WhatsAppChatSummary[][]>([]);

  const clientsQuery = useClients();
  const whatsappStatus = useWhatsAppStatus();
  const whatsappConnected = Boolean(whatsappStatus.data?.connected);

  // Fetch recent chats (last 30 days) for fast initial load
  const recentChatsQuery = useSupabaseChats({ daysBack: 30 });
  // Fetch priority lead chats (Hot/Warm, any age) to ensure they're always visible
  const priorityChatsQuery = usePriorityLeadChats();
  // Keep proxy service chats for real-time updates
  const chatsQuery = useWhatsAppChats();
  const leadLabelQuery = useLeadLabelMap(false, true); // includeArchived = false, enabled = true
  const archivedLeadLabelQuery = useLeadLabelMap(true, true); // includeArchived = true, enabled = true
  const leadLabelMutation = useLeadLabelMutation();
  const convertLeadMutation = useConvertLead();
  const archiveLeadMutation = useArchiveLead();
  const unarchiveLeadMutation = useUnarchiveLead();

  const chatMap = useMemo(() => {
    const map = new Map<string, WhatsAppChatSummary>();

    // 1. Add recent chats (last 30 days) - fast initial load
    if (recentChatsQuery.data) {
      recentChatsQuery.data.forEach((chat) => {
        map.set(chat.jid, chat);
      });
    }

    // 2. Overlay priority lead chats (Hot/Warm, any age) - ensures they're always visible
    if (priorityChatsQuery.data) {
      priorityChatsQuery.data.forEach((chat) => {
        map.set(chat.jid, chat);
      });
    }

    // 3. Add older batches (loaded on-demand)
    olderChatsBatches.flat().forEach((chat) => {
      map.set(chat.jid, chat);
    });

    // 4. Overlay proxy service chats (recent updates, real-time data)
    if (chatsQuery.data?.chats) {
      chatsQuery.data.chats.forEach((chat) => {
        map.set(chat.jid, chat);
      });
    }

    return map;
  }, [recentChatsQuery.data, priorityChatsQuery.data, olderChatsBatches, chatsQuery.data]);

  const clientJidMap = useMemo(() => {
    const map = new Map<string, ClientRecord>();
    if (clientsQuery.data) {
      clientsQuery.data.forEach((client) => {
        // Only use the explicitly stored whatsapp_jid from client_whatsapp_links table
        if (client.whatsapp_jid) {
          map.set(client.whatsapp_jid, client);
        }
      });
    }
    return map;
  }, [clientsQuery.data]);

  const clientRows: ChatListRow[] = useMemo(() => {
    if (!clientsQuery.data) return [];
    return clientsQuery.data
      // Filter: Only include clients with status 'active' or 'past' (exclude 'lead' and 'cold')
      .filter((client) => client.status === 'active' || client.status === 'past')
      .map((client) => {
      // Use only the explicitly stored whatsapp_jid from client_whatsapp_links
      const primaryJid = client.whatsapp_jid;
      const matchedChat = primaryJid ? chatMap.get(primaryJid) : undefined;
      const lastInteractionMs =
        matchedChat?.lastMessage?.timestamp ??
        matchedChat?.updatedAt ??
        (client.last_interaction_at ? new Date(client.last_interaction_at).getTime() : null);
      const status = (client.status ?? 'lead') as ClientStatus;
      return {
        id: client.id,
        clientId: client.id,
        name: client.name,
        phone: client.phone,
        status,
        lastMessage:
          matchedChat?.lastMessage?.text ??
          (whatsappConnected ? 'No WhatsApp messages yet' : 'Connect WhatsApp to sync messages'),
        timestamp: formatRelativeTime(lastInteractionMs),
        timestampMs: lastInteractionMs,
        unread: (matchedChat?.unreadCount ?? 0) > 0,
        unreadCount: matchedChat?.unreadCount ?? 0,
        jid: primaryJid,
        kind: 'client' as const,
        medium: client.medium || 'online',
      };
    }).sort((a, b) => {
      // Sort by timestamp descending (most recent first)
      const aTime = a.timestampMs ?? 0;
      const bTime = b.timestampMs ?? 0;
      return bTime - aTime;
    });
  }, [chatMap, clientsQuery.data, whatsappConnected]);

  const leadRows: ChatListRow[] = useMemo(() => {
    const rows: ChatListRow[] = [];
    const processedJids = new Set<string>();

    // First, add all explicitly labeled leads (hot, warm, cold, new)
    leadLabelQuery.data?.forEach((lead) => {
      // Skip if this lead is linked to a client
      if (clientJidMap.has(lead.jid)) return;

      processedJids.add(lead.jid);
      const chat = chatMap.get(lead.jid);
      const phone = whatsappJidToPhone(lead.jid) ?? lead.jid.split('@')[0] ?? lead.jid;
      const lastTimestamp = chat?.lastMessage?.timestamp ?? chat?.updatedAt;

      rows.push({
        id: lead.jid,
        clientId: undefined,
        name: chat?.name ?? phone,
        phone,
        status: 'lead' as const,
        lastMessage: chat?.lastMessage?.text ?? 'No WhatsApp messages yet',
        timestamp: formatRelativeTime(lastTimestamp),
        timestampMs: lastTimestamp ?? null,
        unread: (chat?.unreadCount ?? 0) > 0,
        unreadCount: chat?.unreadCount ?? 0,
        jid: lead.jid,
        kind: 'lead' as const,
        leadLabel: lead.label,
      });
    });

    // Then, add unlabeled WhatsApp chats (those not in lead_labels and not linked to clients)
    // Iterate through chatMap (which has recent + priority + older chats)
    Array.from(chatMap.values()).forEach((chat) => {
      // Skip if already processed as a labeled lead
      if (processedJids.has(chat.jid)) return;

      // Skip if already a client
      if (clientJidMap.has(chat.jid)) return;

      // Skip group chats
      if (chat.jid.endsWith('@g.us') || chat.jid.endsWith('@lid')) return;

      // Skip archived leads
      const archivedLeadMeta = archivedLeadLabelQuery.map.get(chat.jid);
      if (archivedLeadMeta?.archivedAt) return;

      const phone = whatsappJidToPhone(chat.jid) ?? chat.jid.split('@')[0] ?? chat.jid;
      const lastTimestamp = chat.lastMessage?.timestamp ?? chat.updatedAt;

      rows.push({
        id: chat.jid,
        clientId: undefined,
        name: chat.name,
        phone,
        status: 'lead' as const,
        lastMessage: chat.lastMessage?.text ?? 'No WhatsApp messages yet',
        timestamp: formatRelativeTime(lastTimestamp),
        timestampMs: lastTimestamp,
        unread: (chat.unreadCount ?? 0) > 0,
        unreadCount: chat.unreadCount ?? 0,
        jid: chat.jid,
        kind: 'lead' as const,
        leadLabel: 'new', // Unlabeled chats default to "new"
      });
    });

    // Sort by timestamp descending (most recent first)
    return rows.sort((a, b) => {
      const aTime = a.timestampMs ?? 0;
      const bTime = b.timestampMs ?? 0;
      return bTime - aTime;
    });
  }, [chatMap, clientJidMap, leadLabelQuery.data, archivedLeadLabelQuery.map]);

  const archivedLeadRows: ChatListRow[] = useMemo(() => {
    if (!archivedLeadLabelQuery.data) return [];

    return archivedLeadLabelQuery.data
      .filter((lead) => lead.archivedAt !== null)
      .map((lead) => {
        const chat = chatMap.get(lead.jid);
        const phone = whatsappJidToPhone(lead.jid) ?? lead.jid.split('@')[0] ?? lead.jid;
        const lastTimestamp = chat?.lastMessage?.timestamp ?? chat?.updatedAt;

        return {
          id: lead.jid,
          clientId: undefined,
          name: chat?.name ?? phone,
          phone,
          status: 'lead' as ClientStatus,
          lastMessage: chat?.lastMessage?.text ?? 'No messages',
          timestamp: formatRelativeTime(lastTimestamp),
          timestampMs: lastTimestamp ?? null,
          unread: (chat?.unreadCount ?? 0) > 0,
          unreadCount: chat?.unreadCount ?? 0,
          jid: lead.jid,
          kind: 'lead' as const,
          leadLabel: lead.label,
        };
      })
      .sort((a, b) => {
        // Sort by timestamp descending (most recent first)
        const aTime = a.timestampMs ?? 0;
        const bTime = b.timestampMs ?? 0;
        return bTime - aTime;
      });
  }, [archivedLeadLabelQuery.data, chatMap]);

  const leadStatusCounts = useMemo(() => {
    const counts: Record<LeadLabel, number> = {
      hot: 0,
      warm: 0,
      cold: 0,
      new: 0,
    };

    leadRows.forEach((lead) => {
      const label = lead.leadLabel ?? 'new';
      counts[label]++;
    });

    return counts;
  }, [leadRows]);

  const clientStatusCounts = useMemo(() => {
    const counts: Record<'active' | 'past', number> = {
      active: 0,
      past: 0,
    };

    clientRows.forEach((client) => {
      if (client.status === 'active') {
        counts.active++;
      } else if (client.status === 'past') {
        counts.past++;
      }
    });

    return counts;
  }, [clientRows]);

  const mediumCounts = useMemo(() => {
    const counts: Record<'online' | 'offline', number> = {
      online: 0,
      offline: 0,
    };

    clientRows.forEach((client) => {
      const medium = client.medium || 'online';
      counts[medium]++;
    });

    return counts;
  }, [clientRows]);

  const matchesSearch = useCallback(
    (row: ChatListRow) => {
      const normalizedQuery = searchQuery.trim().toLowerCase();
      return (
        !normalizedQuery ||
        row.name.toLowerCase().includes(normalizedQuery) ||
        row.phone.includes(searchQuery)
      );
    },
    [searchQuery]
  );

  const filteredLeads = useMemo(() => {
    let leads = leadRows.filter((row) => matchesSearch(row));

    // Apply unread filter
    if (showUnreadLeads) {
      leads = leads.filter((row) => row.unread);
    }

    // Apply status filter only if statuses are selected
    if (selectedLeadStatuses.size > 0) {
      leads = leads.filter((row) => {
        const label = row.leadLabel ?? 'new';
        return selectedLeadStatuses.has(label);
      });
    }

    return leads;
  }, [leadRows, matchesSearch, showUnreadLeads, selectedLeadStatuses]);

  const filteredClients = useMemo(() => {
    let clients = clientRows.filter((row) => matchesSearch(row));

    // Apply unread filter
    if (showUnreadClients) {
      clients = clients.filter((row) => row.unread);
    }

    // Apply status filter only if statuses are selected
    if (selectedClientStatuses.size > 0) {
      clients = clients.filter((row) => {
        const status = row.status as 'active' | 'past' | 'cold' | 'lead';
        return (status === 'active' || status === 'past') && selectedClientStatuses.has(status);
      });
    }

    // Apply medium filter only if mediums are selected
    if (selectedMediums.size > 0) {
      clients = clients.filter((row) => {
        const medium = row.medium || 'online';
        return selectedMediums.has(medium);
      });
    }

    return clients;
  }, [clientRows, matchesSearch, showUnreadClients, selectedClientStatuses, selectedMediums]);

  const filteredArchivedLeads = useMemo(
    () => archivedLeadRows.filter((row) => matchesSearch(row)),
    [archivedLeadRows, matchesSearch]
  );

  const unreadCounts = useMemo(() => {
    const unreadLeads = leadRows.filter((row) => row.unread).length;
    const unreadClients = clientRows.filter((row) => row.unread).length;
    return {
      leads: unreadLeads,
      clients: unreadClients,
    };
  }, [clientRows, leadRows]);

  const totalRows = leadRows.length + clientRows.length + archivedLeadRows.length;
  const totalFiltered =
    audienceTab === 'leads' ? filteredLeads.length :
    audienceTab === 'clients' ? filteredClients.length :
    filteredArchivedLeads.length;
  const visibleRows =
    audienceTab === 'leads' ? filteredLeads :
    audienceTab === 'clients' ? filteredClients :
    filteredArchivedLeads;
  const audienceTabs = [
    { key: 'leads' as const, label: 'Leads', count: filteredLeads.length },
    { key: 'clients' as const, label: 'Clients', count: filteredClients.length },
    { key: 'archived' as const, label: 'Archived', count: filteredArchivedLeads.length },
  ];

  const handleSelectChat = (row: ChatListRow) => {
    if (!row.jid) {
      setSelectionError('Add a valid phone number to start a WhatsApp chat.');
      return;
    }
    setSelectionError(null);

    // Save current scroll position before navigating
    const scrollPosition = chatListScrollRef.current?.scrollTop ?? 0;
    sessionStorage.setItem('chatsScrollPosition', scrollPosition.toString());

    navigate(`/whatsapp/chat/${encodeURIComponent(row.jid)}`, {
      state: {
        clientId: row.clientId,
        clientName: row.name,
        clientPhone: row.phone,
        from: 'chats',
        chatsAudienceTab: audienceTab,
      },
    });
  };

  const closeConversionModal = () => {
    setConversionTarget(null);
    setConversionError(null);
  };

  const handleConvertLead = async (payload: { name: string; phone: string }) => {
    if (!conversionTarget?.jid) return;
    setConversionError(null);
    try {
      const response = await convertLeadMutation.mutateAsync({
        name: payload.name,
        phone: payload.phone,
        whatsappJid: conversionTarget.jid ?? undefined,
      });
      const clientId = response.client?.id;
      if (!clientId) {
        setConversionError(
          response.message || 'Unable to convert lead to client',
        );
        return;
      }
      // Get the label from either active or archived leads
      const activeLabel =
        leadLabelQuery.map.get(conversionTarget.jid)?.label ??
        archivedLeadLabelQuery.map.get(conversionTarget.jid)?.label ??
        'new';

      // When converting, also unarchive the lead (set archived to false)
      await leadLabelMutation.mutateAsync({
        jid: conversionTarget.jid,
        convertedClientId: clientId,
        label: activeLabel,
        archived: false, // Unarchive when converting to client
      });
      setLeadActionBanner({
        type: 'success',
        text: response.duplicate
          ? `${response.client?.name ?? 'Client'} already exists.`
          : `${response.client?.name ?? 'Client'} added to clients.`,
      });
      closeConversionModal();
    } catch (error) {
      setConversionError(
        error instanceof Error ? error.message : 'Unable to convert lead',
      );
    }
  };

  const handleArchiveLead = async (jid: string, name: string) => {
    try {
      await archiveLeadMutation.mutateAsync(jid);
      setLeadActionBanner({
        type: 'success',
        text: `${name} archived successfully`,
      });
    } catch (error) {
      setLeadActionBanner({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to archive lead',
      });
    }
  };

  const handleUnarchiveLead = async (jid: string, name: string) => {
    try {
      await unarchiveLeadMutation.mutateAsync(jid);
      setLeadActionBanner({
        type: 'success',
        text: `${name} restored to leads list`,
      });
    } catch (error) {
      setLeadActionBanner({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to unarchive lead',
      });
    }
  };

  const handleLoadOlderChats = async () => {
    setIsLoadingMore(true);

    const currentMaxDays = Math.max(...loadedDayRanges);
    const nextRangeStart = currentMaxDays;
    const nextRangeEnd = currentMaxDays + 30;

    try {
      // Calculate cutoff dates
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - nextRangeStart);

      const endDate = new Date();
      endDate.setDate(endDate.getDate() - nextRangeEnd);

      // Fetch next 30-day batch
      const { data, error } = await supabase
        .from('whatsapp_chats')
        .select('jid, name, last_message_text, last_message_timestamp, unread_count, updated_at')
        .lt('last_message_timestamp', startDate.toISOString())
        .gte('last_message_timestamp', endDate.toISOString())
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Error loading older chats:', error);
        return;
      }

      if (data && data.length > 0) {
        // Map database rows to WhatsAppChatSummary format
        const chats: WhatsAppChatSummary[] = data.map((row) => ({
          jid: row.jid,
          name: row.name,
          lastMessage: row.last_message_text && row.last_message_timestamp
            ? {
                id: '',
                jid: row.jid,
                text: row.last_message_text,
                timestamp: new Date(row.last_message_timestamp).getTime(),
                direction: 'incoming' as const,
              }
            : undefined,
          unreadCount: row.unread_count ?? 0,
          updatedAt: new Date(row.updated_at).getTime(),
        }));

        setOlderChatsBatches(prev => [...prev, chats]);
        setLoadedDayRanges(prev => [...prev, nextRangeEnd]);
      }
    } finally {
      setIsLoadingMore(false);
    }
  };

  const isLoading = clientsQuery.isLoading || (whatsappConnected && chatsQuery.isLoading);

  // Update URL params when tabs change
  useEffect(() => {
    setSearchParams({ audienceTab }, { replace: true });
  }, [audienceTab, setSearchParams]);

  useEffect(() => {
    if (!leadActionBanner) return () => undefined;
    const timeout = window.setTimeout(() => setLeadActionBanner(null), 4000);
    return () => window.clearTimeout(timeout);
  }, [leadActionBanner]);

  // Force re-render every minute to update relative timestamps
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setTick(tick => tick + 1);
    }, 60_000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  // Restore scroll position when navigating back from chat detail
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem('chatsScrollPosition');
    if (savedScrollPosition && chatListScrollRef.current) {
      const scrollTop = parseInt(savedScrollPosition, 10);
      if (!isNaN(scrollTop)) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          if (chatListScrollRef.current) {
            chatListScrollRef.current.scrollTop = scrollTop;
          }
        });
      }
      // Clear the saved position after restoring
      sessionStorage.removeItem('chatsScrollPosition');
    }
  }, [location.pathname]); // Re-run when pathname changes (i.e., navigating back)

  return (
    <div className="h-screen bg-[#F5F5F5] flex flex-col overflow-hidden">
      <div className="bg-white shadow-sm z-20 flex-shrink-0">
        <div className="p-3 space-y-3">
          <h1 className="text-xl font-semibold text-[#212121]">Chats</h1>
          {!whatsappConnected && totalRows === 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-dashed border-[#5B8E87] bg-[#5B8E87]/10 p-2 text-xs text-[#1A7A42]">
              <AlertCircleIcon className="w-4 h-4 flex-shrink-0" />
              <span>Connect WhatsApp to sync past conversations.</span>
              <button
                onClick={() => setShowConnectionModal(true)}
                className="ml-auto text-xs font-semibold text-[#5B8E87]"
              >
                Connect
              </button>
            </div>
          )}
          {!whatsappConnected && totalRows > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-2 text-xs text-blue-800">
              <AlertCircleIcon className="w-4 h-4 flex-shrink-0" />
              <span>Viewing offline. Reconnect WhatsApp to sync new messages.</span>
              <button
                onClick={() => setShowConnectionModal(true)}
                className="ml-auto text-xs font-semibold text-blue-600 hover:text-blue-700"
              >
                Connect
              </button>
            </div>
          )}
          {selectionError && (
            <div className="flex items-center gap-2 rounded-xl bg-red-50 text-red-700 px-3 py-2 text-sm">
              <AlertCircleIcon className="w-4 h-4" />
              <span>{selectionError}</span>
            </div>
          )}
          {leadActionBanner && (
            <div
              className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm border ${
                leadActionBanner.type === 'success'
                  ? 'bg-green-50 text-green-700 border-green-200'
                  : 'bg-red-50 text-red-700 border-red-200'
              }`}
            >
              {leadActionBanner.type === 'success' ? (
                <CheckCircle2Icon className="w-4 h-4" />
              ) : (
                <AlertCircleIcon className="w-4 h-4" />
              )}
              <span>{leadActionBanner.text}</span>
            </div>
          )}
          <div className="relative">
            <SearchIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search by name or number..."
              className="w-full pl-9 pr-3 py-2.5 rounded-lg text-sm border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
            />
          </div>
        </div>
        <div className="px-3 pb-2 border-t border-gray-100 pt-2">
          <div className="bg-gray-100 rounded-xl p-0.5 flex gap-0.5">
            {audienceTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setAudienceTab(tab.key)}
                className={`flex-1 rounded-xl py-1.5 text-xs font-semibold transition-all ${
                  audienceTab === tab.key
                    ? 'bg-white text-[#1A7A42] shadow-sm'
                    : 'text-gray-600'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>
      </div>

      <div ref={chatListScrollRef} className="flex-1 overflow-y-auto px-3 pt-2 space-y-3 pb-20">
        {isLoading && (
          <div className="space-y-3">
            {[...Array(3)].map((_item, index) => (
              <div key={index} className="bg-white rounded-2xl p-5 shadow-sm animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-3" />
                <div className="h-3 bg-gray-200 rounded w-2/3 mb-2" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        )}

        {!isLoading && totalRows > 0 && (
          <section>
            <div className="mb-2">
              {audienceTab === 'leads' && (
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setShowUnreadLeads(!showUnreadLeads)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                      showUnreadLeads
                        ? 'bg-[#5B8E87] text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 border border-gray-200'
                    }`}
                    aria-pressed={showUnreadLeads}
                    aria-label="Filter unread leads"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${showUnreadLeads ? 'bg-white' : 'bg-gray-400'}`} />
                    <span>Unread</span>
                    <span className="text-[10px]">({unreadCounts.leads})</span>
                  </button>
                  <div className="w-px h-4 bg-gray-300" />
                  <LeadStatusFilterChips
                    selectedStatuses={selectedLeadStatuses}
                    onToggleStatus={(status) => {
                      setSelectedLeadStatuses((prev) => {
                        const next = new Set(prev);
                        if (next.has(status)) {
                          next.delete(status);
                        } else {
                          next.add(status);
                        }
                        return next;
                      });
                    }}
                    counts={leadStatusCounts}
                  />
                </div>
              )}

              {audienceTab === 'clients' && (
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setShowUnreadClients(!showUnreadClients)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                      showUnreadClients
                        ? 'bg-[#5B8E87] text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 border border-gray-200'
                    }`}
                    aria-pressed={showUnreadClients}
                    aria-label="Filter unread clients"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${showUnreadClients ? 'bg-white' : 'bg-gray-400'}`} />
                    <span>Unread</span>
                    <span className="text-[10px]">({unreadCounts.clients})</span>
                  </button>
                  <div className="w-px h-4 bg-gray-300" />
                  <ClientStatusFilterChips
                    selectedStatuses={selectedClientStatuses}
                    onToggleStatus={(status) => {
                      setSelectedClientStatuses((prev) => {
                        const next = new Set(prev);
                        if (next.has(status)) {
                          next.delete(status);
                        } else {
                          next.add(status);
                        }
                        return next;
                      });
                    }}
                    counts={clientStatusCounts}
                  />
                  <div className="w-px h-4 bg-gray-300" />
                  <MediumFilterChips
                    selectedMediums={selectedMediums}
                    onToggleMedium={(medium) => {
                      setSelectedMediums((prev) => {
                        const next = new Set(prev);
                        if (next.has(medium)) {
                          next.delete(medium);
                        } else {
                          next.add(medium);
                        }
                        return next;
                      });
                    }}
                    counts={mediumCounts}
                  />
                </div>
              )}

              {audienceTab === 'archived' && (
                <h2 className="text-sm font-semibold text-[#212121] mb-2">Archived Leads</h2>
              )}
            </div>

            {totalFiltered > 0 && (
              <div className="space-y-2">
              {visibleRows.map((chat) => {
                const isConverting = convertLeadMutation.isPending && conversionTarget?.jid === chat.jid;
                const isArchiving = archiveLeadMutation.isPending;
                const isUnarchiving = unarchiveLeadMutation.isPending;
                const isArchivedView = audienceTab === 'archived';

                return (
                  <div key={chat.id} className="space-y-2">
                    <div onClick={() => handleSelectChat(chat)}>
                      <ChatRow
                        chat={chat}
                        unreadCount={chat.unreadCount}
                        isOffline={!whatsappConnected}
                        onConvert={chat.kind === 'lead' && !isArchivedView ? () => {
                          setConversionError(null);
                          setConversionTarget(chat);
                        } : undefined}
                        converting={isConverting}
                        onArchive={chat.kind === 'lead' && !isArchivedView && chat.jid ? () => {
                          handleArchiveLead(chat.jid!, chat.name);
                        } : undefined}
                        archiving={isArchiving}
                        onUnarchive={chat.kind === 'lead' && isArchivedView && chat.jid ? () => {
                          handleUnarchiveLead(chat.jid!, chat.name);
                        } : undefined}
                        unarchiving={isUnarchiving}
                      />
                    </div>
                  </div>
                );
              })}
              </div>
            )}

            {/* Load Older Chats Button */}
            {audienceTab === 'leads' && totalFiltered > 0 && !isLoading && (
              <div className="py-4 text-center">
                <button
                  onClick={handleLoadOlderChats}
                  disabled={isLoadingMore}
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 hover:text-gray-900 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  {isLoadingMore ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading...
                    </span>
                  ) : (
                    `Load older chats (${Math.max(...loadedDayRanges)}+ days ago)`
                  )}
                </button>
              </div>
            )}

            {totalFiltered === 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
                <p className="text-gray-600 mb-1">
                  {audienceTab === 'leads' && showUnreadLeads
                    ? 'No unread leads.'
                    : audienceTab === 'clients' && showUnreadClients
                    ? 'No unread client messages.'
                    : audienceTab === 'leads'
                    ? 'No leads match your filters.'
                    : audienceTab === 'clients'
                    ? 'No clients match your filters.'
                    : 'No archived leads match your filters.'}
                </p>
                <p className="text-sm text-gray-500">
                  {(audienceTab === 'leads' && showUnreadLeads) || (audienceTab === 'clients' && showUnreadClients)
                    ? 'All caught up! No unread messages.'
                    : 'Try a different search term or filter.'}
                </p>
              </div>
            )}
          </section>
        )}

        {!isLoading && totalRows === 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">💬</span>
            </div>
            <h3 className="text-lg font-semibold text-[#212121] mb-2">No WhatsApp conversations yet</h3>
            <p className="text-gray-600 mb-6">
              Add a client or wait for new leads to message you on WhatsApp.
            </p>
            <button
              onClick={() => navigate('/add-client')}
              className="bg-[#5B8E87] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#4F7A74] transition-colors"
            >
              ➕ Add Client
            </button>
          </div>
        )}
      </div>

      <button
        onClick={() => navigate('/add-client')}
        className="fixed bottom-24 right-6 bg-[#5B8E87] text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-[#4F7A74] transition-all active:scale-95 z-30"
      >
        <PlusIcon className="w-6 h-6" />
      </button>
      <BottomNav />
      {showConnectionModal && (
        <WhatsAppConnectionModal
          onClose={() => setShowConnectionModal(false)}
          onSuccess={() => {
            setShowConnectionModal(false);
            chatsQuery.refetch();
          }}
        />
      )}
      <LeadConversionModal
        open={Boolean(conversionTarget)}
        defaultName={conversionTarget?.name ?? ''}
        defaultPhone={conversionTarget?.phone ?? ''}
        onClose={closeConversionModal}
        onConvert={handleConvertLead}
        isSubmitting={convertLeadMutation.isPending}
        error={conversionError}
      />
    </div>
  );
}
